import React from 'react';
import type { DeviceInfo } from '@/modules/security/types/interfaces/DeviceSession';
import { maskEmail } from '@/shared/utils/helpers';

interface DeviceBlockedProps {
  deviceInfo?: DeviceInfo | null;
  email?: string;
  onClose?: () => void;
}

/**
 * Componente para mostrar el bloqueo de acceso por dispositivo no conocido
 */
export const DeviceBlocked: React.FC<DeviceBlockedProps> = ({ deviceInfo, email, onClose }) => {

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="relative mx-4 w-full max-w-md animate-fade-in">
        {/* Card principal */}
        <div className="rounded-2xl bg-white shadow-2xl">
          {/* Header con icono */}
          <div className="flex flex-col items-center border-b border-gray-200 px-6 py-4">
            {/* Icono de bloqueo */}
            <div className="mb-2 flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
              <svg
                className="h-10 w-10 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>

            {/* Título */}
            <h2 className="text-2xl font-bold text-gray-900 w-full text-center">
              Acceso bloqueado por verificación de seguridad
            </h2>
          </div>

          {/* Contenido */}
          <div className="px-6 py-0">
            <div className="mb-6 text-center">
              <p className="text-base font-bold text-gray-700 leading-relaxed">
                Hemos bloqueado temporalmente este intento de acceso porque el dispositivo no ha sido reconocido como confiable.<br/><br/>
              </p>
              <p className="text-base text-gray-700 leading-relaxed">
                👉 <span className='font-bold'>Fue enviado un correo electrónico de verificación</span> a la dirección{!email ? ' proporcionada' : ':'} <br/><span className='text-blue-700 font-mono'>{email ? maskEmail(email) : ''}</span><br/><br/>
              </p>
              <p className="text-base text-gray-700 leading-relaxed">
                Debe <span className='font-bold'>revisar su correo y confirmar el acceso</span> para autorizar este dispositivo.<br/>                
              </p>
            </div>

            {/* Información del dispositivo si está disponible */}
            {/* {deviceInfo && (
              <div className="mb-6 rounded-lg bg-gray-50 p-4">
                <h3 className="mb-3 text-sm font-semibold text-gray-900">
                  Información del dispositivo:
                </h3>
                <div className="space-y-2 text-sm">
                  {deviceInfo.deviceName && (
                    <div className="flex items-start">
                      <span className="mr-2 font-medium text-gray-700">Dispositivo:</span>
                      <span className="text-gray-600">{deviceInfo.deviceName}</span>
                    </div>
                  )}
                  {deviceInfo.browser && (
                    <div className="flex items-start">
                      <span className="mr-2 font-medium text-gray-700">Navegador:</span>
                      <span className="text-gray-600">{deviceInfo.browser}</span>
                    </div>
                  )}
                  {deviceInfo.operatingSystem && (
                    <div className="flex items-start">
                      <span className="mr-2 font-medium text-gray-700">Sistema:</span>
                      <span className="text-gray-600">{deviceInfo.operatingSystem}</span>
                    </div>
                  )}
                  {deviceInfo.ipAddress && (
                    <div className="flex items-start">
                      <span className="mr-2 font-medium text-gray-700">IP:</span>
                      <span className="text-gray-600">{deviceInfo.ipAddress}</span>
                    </div>
                  )}
                  {deviceInfo.location && (
                    <div className="flex items-start">
                      <span className="mr-2 font-medium text-gray-700">Ubicación:</span>
                      <span className="text-gray-600">{deviceInfo.location}</span>
                    </div>
                  )}
                </div>
              </div>
            )} */}

            {/* Mensaje informativo */}
            {/* <div className="mb-6 rounded-lg bg-blue-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-blue-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    Por motivos de seguridad, debe verificar su identidad desde un dispositivo conocido.
                  </p>
                </div>
              </div>
            </div> */}

            {/* Instrucciones */}
            <div className="mb-4 space-y-2 text-sm text-gray-600">
              <p className="font-medium text-gray-700"><span className='font-bold'>Para continuar:</span></p>
              <ul className="ml-4 list-disc space-y-1">
                <li>Revise su correo electrónico y siga las instrucciones de validación</li>
                <li>Una vez confirmado, podrá ingresar nuevamente al sistema</li>
                <li>Si no reconoce este intento, favor de negar el acceso utilizando la opción incluida en el correo recibido. En caso de no haber recibido el correo, contacte al soporte técnico</li>
              </ul>
            </div>
          </div>

          {/* Footer con botón */}
          <div className="border-t border-gray-200 px-6 pt-2 pb-4">
            <button
              onClick={onClose}
              className="w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Entendido
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
